import Layout from '../components/Layout';
import { PARTNER_TYPES } from '../mock';
import { Store, Truck, Building2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const iconMap = { Store, Truck, Building2 };

export default function PartnerPage() {
  const [selected, setSelected] = useState('retailer');
  const [form, setForm] = useState({ name: '', email: '', phone: '', city: '', business_name: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.city) {
      toast({ title: 'Fill required fields' });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/partner-applications`, { ...form, type: selected });
      setSubmitted(true);
      toast({ title: 'Application received', description: 'Our team will call you within 24 hours.' });
    } catch (e) {
      toast({ title: 'Failed to submit', description: e.response?.data?.detail || 'Try again' });
    } finally { setSending(false); }
  };

  return (
    <Layout>
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <p className="eyebrow mb-4">Become a partner</p>
            <h1 className="h-display text-4xl md:text-6xl">Grow Your Automotive Business with <em className="font-serif-display italic font-normal text-navy">Connect Near Me</em></h1>
            <p className="mt-5 text-neutral-600 text-lg">Pick your fit: sell at retail, distribute across a district, or run a full outlet. We supply, you scale.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mt-14">
            {PARTNER_TYPES.map((p) => {
              const Ic = iconMap[p.icon] || Store;
              const isActive = selected === p.id;
              return (
                <button key={p.id} onClick={() => setSelected(p.id)} className={`card-soft p-6 text-left transition-all ${isActive ? 'ring-2 ring-navy bg-navy text-white border-navy' : 'hover:-translate-y-0.5'}`}>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${isActive ? 'bg-white/15' : 'bg-navy/5 text-navy'}`}>
                    <Ic size={22} />
                  </div>
                  <h3 className="text-2xl font-semibold">{p.title}</h3>
                  <p className={`text-sm mt-1 ${isActive ? 'text-blue-200' : 'text-neutral-500'}`}>{p.tagline}</p>
                  <div className={`mt-3 text-xs font-medium tracking-wide uppercase ${isActive ? 'text-blue-100' : 'text-navy'}`}>{p.price}</div>
                  <ul className="mt-4 space-y-2">
                    {p.perks.map((it) => (
                      <li key={it} className={`text-sm flex gap-2 items-start ${isActive ? 'text-blue-100' : 'text-neutral-700'}`}>
                        <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5" /> {it}
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>

          <div id="apply" className="mt-16 card-soft p-8 md:p-10 grid md:grid-cols-2 gap-10">
            <div>
              <h2 className="h-display text-3xl md:text-4xl">Apply as a <em className="font-serif-display italic font-normal text-navy">{PARTNER_TYPES.find((p) => p.id === selected)?.title}</em></h2>
              <p className="mt-3 text-neutral-600">Fill the form and our partnership team will get in touch within 24 hours to discuss territory, pricing and onboarding.</p>
              <ul className="mt-6 space-y-2 text-sm text-neutral-700">
                <li className="flex gap-2 items-center"><CheckCircle2 size={14} className="text-navy" /> Same-day dispatch on all bulk orders</li>
                <li className="flex gap-2 items-center"><CheckCircle2 size={14} className="text-navy" /> Marketing kit + demo tag included</li>
                <li className="flex gap-2 items-center"><CheckCircle2 size={14} className="text-navy" /> Dedicated relationship manager</li>
              </ul>
              <div className="mt-6 p-4 rounded-xl bg-navy/5 text-sm text-navy">
                Already a partner? <a href="/partner/login" className="underline font-semibold">Partner login</a>
              </div>
            </div>

            {submitted ? (
              <div className="flex flex-col items-center justify-center text-center bg-navy text-white rounded-2xl p-10">
                <CheckCircle2 size={42} className="mb-4" />
                <h3 className="h-display text-2xl">Application received</h3>
                <p className="text-blue-100 mt-2 text-sm">Our team will reach out within 24 hours. In the meantime, feel free to explore the app.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-medium">Full name *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none" /></div>
                  <div><label className="text-xs font-medium">Business name</label><input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-medium">Phone *</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none" /></div>
                  <div><label className="text-xs font-medium">Email *</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none" /></div>
                </div>
                <div><label className="text-xs font-medium">City *</label><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none" /></div>
                <div><label className="text-xs font-medium">Tell us about your business</label><textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none resize-none" /></div>
                <button disabled={sending} type="submit" className="btn-primary w-full justify-center disabled:opacity-60">{sending ? 'Submitting...' : 'Submit application'} <ArrowRight size={16} /></button>
              </form>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
